import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { COLORS, AFFIRMATIONS } from '../constants';
import { Card, Button, Badge, StatCard } from './UI';

const XP_LEVELS = [0, 100, 250, 500, 800, 1200, 1700, 2300, 3000, 4000];

function getLevel(xp) {
  let level = 1;
  for (let i = 1; i < XP_LEVELS.length; i++) {
    if (xp >= XP_LEVELS[i]) level = i + 1;
    else break;
  }
  return level;
}

function getLevelProgress(xp) {
  const level = getLevel(xp);
  if (level >= 10) return { level, currentXP: xp, nextXP: XP_LEVELS[9], pct: 100 };
  const currentLevelXP = XP_LEVELS[level - 1];
  const nextLevelXP = XP_LEVELS[level];
  const pct = ((xp - currentLevelXP) / (nextLevelXP - currentLevelXP)) * 100;
  return { level, currentXP: xp, nextXP: nextLevelXP, pct };
}

const SUGGEST_TASK_EMOJIS  = ["⭐","📚","🧹","🐾","🏃","🍽️","🛁","🎨","🎵","💪"];
const SUGGEST_REWARD_EMOJIS = ["🎮","🍕","🎬","🛍️","🎉","🍦","🎢","📱","🎁","🏖️"];
const GOAL_EMOJIS = ["🎯","🎮","🚴","🏖️","🎨","🧸","📚","⚽","🎤","🌟","🦄","🍕","🏄","🎸","🐶"];

// Maps a task's coin value to a rough time estimate shown below each mission card
function estTime(pts) {
  if (pts <= 10) return '~5 min';
  if (pts <= 20) return '~10 min';
  if (pts <= 30) return '~15 min';
  return '~20 min';
}

// Pre-seeded confetti data — deterministic so it never jitters on re-render
const CONFETTI_COLORS = ['#FFD93D','#F59E0B','#EF4444','#10B981','#8B5CF6','#3B82F6','#F97316','#EC4899','#FCD34D','#A3E635'];
const CONFETTI = Array.from({ length: 52 }, (_, i) => ({
  color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  left:  (i * 17 + 11) % 100,
  delay: ((i * 0.19) % 2.6).toFixed(2),
  dur:   (1.5 + (i * 0.08) % 1.8).toFixed(2),
  size:  6 + (i * 3) % 10,
  round: i % 3 === 0,
  flip:  i % 2 === 0,
}));

const DARK_COLORS = {
  ...COLORS,
  bg: "#0A0A0A",
  surface: "#111827",
  surfaceGreen: "#111827",
  text: "#F9FAFB",
  textMuted: "#9CA3AF",
  textLight: "#6B7280",
  border: "#1F2937",
  accentLighter: "#1A2535",
  warningLight: "#2D1F0E",
  successLight: "#0F2318",
  white: "#111827",
};

export default function KidDashboard({ child, tasks, rewards, onTaskDone, onLogout }) {
  const [kidScreen, setKidScreen] = useState("home");
  const [celebrateTask, setCelebrateTask] = useState(null);
  const [journalEntry, setJournalEntry] = useState("");
  const [journalSubmitted, setJournalSubmitted] = useState(false);
  const [journalSaving, setJournalSaving] = useState(false);
  const [todayJournal, setTodayJournal] = useState(null);
  const [selectedMood, setSelectedMood] = useState(null);
  const [pastJournals, setPastJournals] = useState([]);
  const [localUsername, setLocalUsername] = useState(child.username || '');
  const [editingUsername, setEditingUsername] = useState(false);
  const [usernameInput, setUsernameInput] = useState('');
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('kid_dark_mode') === 'true');
  const [showSuggestMission, setShowSuggestMission] = useState(false);
  const [suggestMissionForm, setSuggestMissionForm] = useState({ name: '', emoji: '⭐', points: '10' });
  const [suggestMissionSaving, setSuggestMissionSaving] = useState(false);
  const [showSuggestDrop, setShowSuggestDrop] = useState(false);
  const [suggestDropForm, setSuggestDropForm] = useState({ name: '', emoji: '🎮', points: '30' });
  const [suggestDropSaving, setSuggestDropSaving] = useState(false);
  const [showChangePIN, setShowChangePIN] = useState(false);
  const [changePINForm, setChangePINForm] = useState({ current: '', newPin: '', confirm: '' });
  const [pinChangeSaving, setPinChangeSaving] = useState(false);
  const [pinChangeError, setPinChangeError] = useState('');
  const [pinChangeSuccess, setPinChangeSuccess] = useState(false);
  const [ringAnimated, setRingAnimated] = useState(false);
  const [showAllDoneCelebration, setShowAllDoneCelebration] = useState(false);
  const [sessionXP, setSessionXP] = useState(0);
  const [sessionCoins, setSessionCoins] = useState(0);
  const [walletCoins, setWalletCoins] = useState(child.points || 0);
  const [coinDelta, setCoinDelta] = useState(null);
  const [coinDeltaKey, setCoinDeltaKey] = useState(0);

  // ── GOAT Savings Pod / Personal Goals ───────────────────────
  const [goals, setGoals] = useState([]);
  const [savePodGoalId, setSavePodGoalId] = useState(null); // which goal has the input open
  const [saveAmount, setSaveAmount] = useState('');
  const [saveError, setSaveError] = useState('');
  const [savePodSaving, setSavePodSaving] = useState(false);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [addGoalForm, setAddGoalForm] = useState({ name: '', emoji: '🎯', target: '50' });
  const [addGoalSaving, setAddGoalSaving] = useState(false);

  const today = new Date().toDateString();
  const C = darkMode ? DARK_COLORS : COLORS;

  // Trigger ring fill-in animation whenever the home tab is shown
  useEffect(() => {
    if (kidScreen === 'home') {
      setRingAnimated(false);
      const t = setTimeout(() => setRingAnimated(true), 80);
      return () => clearTimeout(t);
    }
  }, [kidScreen]);

  // Show a floating +N gold label and update the wallet display
  const triggerCoinAnim = (amount) => {
    setCoinDelta(amount);
    setCoinDeltaKey(k => k + 1);
    setTimeout(() => setCoinDelta(null), 950);
  };

  // Poll Supabase every 30 s to pick up coins awarded by parent approvals
  useEffect(() => {
    const id = setInterval(async () => {
      const { data } = await supabase
        .from('children')
        .select('points')
        .eq('id', child.id)
        .single();
      if (data && data.points > walletCoins) {
        triggerCoinAnim(data.points - walletCoins);
        setWalletCoins(data.points);
      }
    }, 30000);
    return () => clearInterval(id);
  }, [child.id, walletCoins]);

  // Load goals whenever the rewards tab is activated
  const loadGoals = async () => {
    const { data } = await supabase
      .from('kid_goals')
      .select('*')
      .eq('child_id', child.id)
      .order('created_at');
    setGoals(data || []);
  };

  useEffect(() => {
    if (kidScreen === 'rewards') loadGoals();
  }, [kidScreen]); // eslint-disable-line

  const handleSaveCoins = async (goal) => {
    const amount = parseInt(saveAmount, 10);
    setSaveError('');
    if (!amount || amount < 1) { setSaveError('Enter an amount of at least 1 coin.'); return; }
    if (amount > walletCoins) { setSaveError("You don't have enough coins. Earn more by completing missions!"); return; }
    const remaining = goal.target_coins - goal.saved_coins;
    if (amount > remaining) { setSaveError(`That's more than you need — only ${remaining} more coin${remaining === 1 ? '' : 's'} to reach this goal!`); return; }
    setSavePodSaving(true);
    const newSaved = goal.saved_coins + amount;
    await Promise.all([
      supabase.from('kid_goals').update({ saved_coins: newSaved }).eq('id', goal.id),
      supabase.from('children').update({ points: walletCoins - amount }).eq('id', child.id),
    ]);
    setGoals(prev => prev.map(g => g.id === goal.id ? { ...g, saved_coins: newSaved } : g));
    setWalletCoins(prev => prev - amount);
    setSavePodGoalId(null);
    setSaveAmount('');
    setSaveError('');
    setSavePodSaving(false);
  };

  const submitGoal = async () => {
    if (!addGoalForm.name.trim() || addGoalSaving) return;
    setAddGoalSaving(true);
    const target = Math.max(1, parseInt(addGoalForm.target, 10) || 50);
    await supabase.from('kid_goals').insert({
      child_id: child.id,
      parent_id: child.parent_id,
      name: addGoalForm.name.trim(),
      emoji: addGoalForm.emoji,
      target_coins: target,
    });
    setAddGoalSaving(false);
    setShowAddGoal(false);
    setAddGoalForm({ name: '', emoji: '🎯', target: '50' });
    loadGoals();
  };

  const loadTodayJournal = async () => {
    const { data: today_data } = await supabase.from('journals').select('*').eq('child_id', child.id).eq('date', today).maybeSingle();
    if (today_data) { setTodayJournal(today_data); setJournalSubmitted(true); setSelectedMood(today_data.mood || null); }
    const { data: past } = await supabase.from('journals').select('*').eq('child_id', child.id).order('created_at', { ascending: false }).limit(10);
    setPastJournals(past || []);
  };

  const submitJournal = async () => {
    if (!journalEntry.trim() || journalSaving) return;
    setJournalSaving(true);
    await supabase.from('journals').insert({ child_id: child.id, parent_id: child.parent_id, entry: journalEntry, mood: selectedMood, date: today });
    await supabase.from('children').update({ points: (child.points || 0) + 5 }).eq('id', child.id);
    await supabase.rpc('increment_xp', { child_id: child.id, amount: 5 });
    setJournalSubmitted(true);
    setJournalSaving(false);
    setWalletCoins(prev => prev + 5);
    triggerCoinAnim(5);
    loadTodayJournal();
  };

  const handleMeScreen = () => {
    setKidScreen("me");
    loadTodayJournal();
  };

  const saveUsername = async () => {
    const cleaned = usernameInput.replace(/[^a-zA-Z0-9]/g, '').slice(0, 20);
    if (!cleaned) return;
    setUsernameSaving(true);
    await supabase.from('children').update({ username: cleaned }).eq('id', child.id);
    setLocalUsername(cleaned);
    setEditingUsername(false);
    setUsernameSaving(false);
  };

  const submitSuggestedMission = async () => {
    if (!suggestMissionForm.name.trim() || suggestMissionSaving) return;
    setSuggestMissionSaving(true);
    await supabase.from('tasks').insert({
      child_id: child.id, parent_id: child.parent_id,
      name: suggestMissionForm.name.trim(), emoji: suggestMissionForm.emoji,
      points: Math.min(parseInt(suggestMissionForm.points) || 10, 50),
      frequency: 'Daily', status: 'pending_approval',
      suggested_by_kid: true, suggestion_status: 'pending',
    });
    setSuggestMissionSaving(false);
    window.location.reload();
  };

  const submitSuggestedDrop = async () => {
    if (!suggestDropForm.name.trim() || suggestDropSaving) return;
    setSuggestDropSaving(true);
    await supabase.from('rewards').insert({
      child_id: child.id, parent_id: child.parent_id,
      name: suggestDropForm.name.trim(), emoji: suggestDropForm.emoji,
      points: parseInt(suggestDropForm.points) || 30,
      suggested_by_kid: true, suggestion_status: 'pending',
    });
    setSuggestDropSaving(false);
    window.location.reload();
  };

  const changePIN = async () => {
    setPinChangeError('');
    if (changePINForm.current !== child.pin) return setPinChangeError('Current PIN is incorrect.');
    if (changePINForm.newPin.length !== 4) return setPinChangeError('New PIN must be exactly 4 digits.');
    if (changePINForm.newPin !== changePINForm.confirm) return setPinChangeError('PINs do not match. Try again.');
    setPinChangeSaving(true);
    await supabase.from('children').update({ pin: changePINForm.newPin }).eq('id', child.id);
    setPinChangeSaving(false);
    setPinChangeSuccess(true);
    setTimeout(() => { setPinChangeSuccess(false); setShowChangePIN(false); setChangePINForm({ current: '', newPin: '', confirm: '' }); }, 2000);
  };

  const myTasks = tasks.filter(t => t.child_id === child.id);
  const activeTasks = myTasks.filter(t => t.status === "active");
  const pendingTasks = myTasks.filter(t => t.status === "pending");
  const completedTasks = myTasks.filter(t => t.status === "completed");
  // Lowest-point active task — highlighted as "Quick Win" to build momentum
  const quickWinTask = activeTasks.length > 0
    ? [...activeTasks].sort((a, b) => a.points - b.points)[0]
    : null;
  const myRewards = rewards.filter(r => r.child_id === child.id);
  const suggestedTasks   = myTasks.filter(t => t.suggested_by_kid && t.suggestion_status === 'pending');
  const activeRewards    = myRewards.filter(r => !(r.suggested_by_kid && r.suggestion_status === 'pending'));
  const suggestedRewards = myRewards.filter(r => r.suggested_by_kid && r.suggestion_status === 'pending');
  const totalPoints = child.points || 0;
  const nextReward = myRewards.filter(r => r.points > totalPoints).sort((a, b) => a.points - b.points)[0];

  const BADGES = [
    { id: "first_step", emoji: "👟", name: "First Step", desc: "Complete your first task", earned: completedTasks.length >= 1 },
    { id: "high_five", emoji: "🖐️", name: "High Five", desc: "Complete 5 tasks", earned: completedTasks.length >= 5 },
    { id: "ten_strong", emoji: "💪", name: "Ten Strong", desc: "Complete 10 tasks", earned: completedTasks.length >= 10 },
    { id: "point_collector", emoji: "⭐", name: "Coin Collector", desc: "Earn 50 coins", earned: totalPoints >= 50 },
    { id: "star_power", emoji: "🌟", name: "Star Power", desc: "Earn 100 coins", earned: totalPoints >= 100 },
    { id: "superstar", emoji: "🏆", name: "Superstar", desc: "Earn 250 coins", earned: totalPoints >= 250 },
    { id: "task_master", emoji: "🎯", name: "Task Master", desc: "Have 3+ active tasks", earned: myTasks.length >= 3 },
    { id: "on_a_roll", emoji: "🔥", name: "On A Roll", desc: "Complete 3 tasks in a row", earned: completedTasks.length >= 3 },
    { id: "journal_writer", emoji: "📓", name: "Journal Writer", desc: "Write in your journal", earned: false },
    { id: "mood_checker", emoji: "😊", name: "Mood Checker", desc: "Complete a mood check-in", earned: !!child.last_mood_date },
  ];
  const earnedBadges = BADGES.filter(b => b.earned);
  const unearnedBadges = BADGES.filter(b => !b.earned);

  const handleTaskDone = async (task) => {
    await supabase.from('tasks').update({ status: 'pending' }).eq('id', task.id);

    // +10 XP for marking a task done; +20 bonus if this was the last active task
    const isLastActive = activeTasks.filter(t => t.id !== task.id).length === 0;
    let xpGain = 10 + (isLastActive ? 20 : 0);
    const childUpdate = {};
    let weeklyBonusEarned = false;

    if (isLastActive) {
      // Weekly challenge: track days where all tasks were completed
      const dayOfWeek = new Date().getDay(); // 0=Sun
      const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const monday = new Date();
      monday.setDate(monday.getDate() - daysFromMonday);
      const mondayISO = monday.toISOString().split('T')[0];

      const isCurrentWeek = child.weekly_challenge_start === mondayISO;
      const currentCount = isCurrentWeek ? (child.weekly_tasks_completed || 0) : 0;
      const newCount = currentCount + 1;
      // Award +100 XP exactly when count first reaches 7; clamp to 7 to prevent re-award
      if (newCount === 7) { xpGain += 100; weeklyBonusEarned = true; }
      childUpdate.weekly_challenge_start = mondayISO;
      childUpdate.weekly_tasks_completed = Math.min(newCount, 7);
    }

    if (Object.keys(childUpdate).length > 0) {
      await supabase.from('children').update(childUpdate).eq('id', child.id);
    }
    await supabase.rpc('increment_xp', { child_id: child.id, amount: 10 });
    if (isLastActive) {
      await supabase.rpc('increment_xp', { child_id: child.id, amount: 20 });
    }
    if (weeklyBonusEarned) {
      await supabase.rpc('increment_xp', { child_id: child.id, amount: 100 });
    }

    try {
      await supabase.functions.invoke('notify-parent', {
        body: {
          task_name: task.name,
          child_name: child.name,
          parent_id: child.parent_id,
        },
      });
    } catch (err) {
      console.error('Failed to send parent notification:', err);
    }

    // Accumulate session earnings so the celebration screen shows correct totals
    setSessionXP(prev => prev + xpGain);
    setSessionCoins(prev => prev + task.points);

    onTaskDone(task);

    if (isLastActive) {
      // Full-screen "all missions done" celebration — fires once per calendar day per child
      const celebKey = `thrivetrack_celebration_${child.id}`;
      if (localStorage.getItem(celebKey) !== today) {
        localStorage.setItem(celebKey, today);
        setShowAllDoneCelebration(true);
        setTimeout(() => setShowAllDoneCelebration(false), 5000);
      } else {
        // Already celebrated today (e.g. after a page reload) — regular per-task popup
        setCelebrateTask(task);
        setTimeout(() => setCelebrateTask(null), 2500);
      }
    } else {
      setCelebrateTask(task);
      setTimeout(() => setCelebrateTask(null), 2500);
    }
  };

  return (
    <div className={darkMode ? 'kid-dark-mode' : ''} style={{ minHeight: "100vh", background: C.bg, maxWidth: 480, margin: "0 auto", paddingBottom: 80 }}>
      <style>{`
        @keyframes popIn{0%{transform:scale(0.5);opacity:0}70%{transform:scale(1.15)}100%{transform:scale(1);opacity:1}}
        @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
        @keyframes weeklyPulse{0%,100%{box-shadow:0 0 40px rgba(251,191,36,0.45)}50%{box-shadow:0 0 70px rgba(251,191,36,0.75),0 0 100px rgba(251,191,36,0.25)}}
        @keyframes ringPulse{0%,100%{filter:drop-shadow(0 0 8px rgba(245,158,11,0.65))}50%{filter:drop-shadow(0 0 24px rgba(251,191,36,1))}}
        @keyframes confettiFall{0%{transform:translateY(-30px) translateX(0) rotate(0deg);opacity:1}45%{opacity:1}80%{transform:translateY(85vh) translateX(18px) rotate(540deg);opacity:0.7}100%{transform:translateY(108vh) translateX(-8px) rotate(720deg);opacity:0}}
        @keyframes trophyBounce{0%{transform:scale(0) translateY(-40px);opacity:0}55%{transform:scale(1.3) translateY(10px);opacity:1}75%{transform:scale(0.88) translateY(-6px)}90%{transform:scale(1.06) translateY(2px)}100%{transform:scale(1) translateY(0);opacity:1}}
        @keyframes celebFadeIn{from{opacity:0}to{opacity:1}}
        @keyframes celebSlideUp{from{transform:translateY(32px);opacity:0}to{transform:translateY(0);opacity:1}}
        @keyframes coinPop{0%{transform:translateY(0) scale(1);opacity:1}55%{transform:translateY(-22px) scale(1.25);opacity:0.85}100%{transform:translateY(-40px) scale(0.9);opacity:0}}
        @keyframes nudgeDrop{from{transform:translateY(-10px);opacity:0}to{transform:translateY(0);opacity:1}}
        .kid-dark-mode .kid-ui-card { background: #111827 !important; border-color: #1F2937 !important; }
        .kid-dark-mode .kid-ui-statcard { background: #111827 !important; border-color: #1F2937 !important; }
        .kid-dark-mode .kid-ui-statcard div:last-child { color: #9CA3AF !important; }
        .kid-dark-mode input, .kid-dark-mode textarea { background: #1F2937 !important; color: #F9FAFB !important; border-color: #374151 !important; }
        .kid-dark-mode input::placeholder, .kid-dark-mode textarea::placeholder { color: #6B7280 !important; }
      `}</style>

      {/* ── All Missions Complete — Full-Screen Celebration ── */}
      {showAllDoneCelebration && (() => {
        const displayName = localUsername ? `@${localUsername}` : child.name;
        const streak = child.streak || 0;
        return (
          <div style={{ position: 'fixed', inset: 0, background: '#0A0A0A', zIndex: 500, overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', animation: 'celebFadeIn 0.35s ease' }}>

            {/* Confetti rain */}
            {CONFETTI.map((p, i) => (
              <div key={i} style={{
                position: 'absolute',
                left: `${p.left}%`,
                top: -20,
                width: p.size,
                height: p.round ? p.size : Math.ceil(p.size * 0.45),
                borderRadius: p.round ? '50%' : 3,
                background: p.color,
                animation: `confettiFall ${p.dur}s ${p.delay}s ease-in infinite`,
                pointerEvents: 'none',
              }} />
            ))}

            {/* Content card — sits above confetti */}
            <div style={{ position: 'relative', zIndex: 10, textAlign: 'center', padding: '0 28px', animation: 'celebSlideUp 0.5s 0.15s ease both' }}>

              {/* Trophy */}
              <div style={{ fontSize: 88, lineHeight: 1, marginBottom: 16, animation: 'trophyBounce 0.7s 0.2s ease both', display: 'inline-block' }}>🏆</div>

              {/* Heading */}
              <div style={{ fontFamily: 'Fredoka One, sans-serif', fontSize: 38, color: '#FFD93D', lineHeight: 1.1, marginBottom: 10, textShadow: '0 2px 20px rgba(251,191,36,0.5)' }}>
                All Missions<br />Complete!
              </div>

              {/* Subtext */}
              <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 17, color: '#F9FAFB', marginBottom: 32, opacity: 0.9 }}>
                You crushed it today, {displayName} 🔥
              </div>

              {/* Stats row */}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 36 }}>
                {[
                  { label: 'XP TODAY',  value: `+${sessionXP}`,    color: '#F59E0B' },
                  { label: 'COINS ⭐',  value: `+${sessionCoins}`,  color: '#FFD93D' },
                  { label: 'STREAK',    value: `${streak}🔥`,       color: '#F97316' },
                ].map(stat => (
                  <div key={stat.label} style={{ background: '#111827', borderRadius: 16, padding: '14px 16px', textAlign: 'center', minWidth: 82, border: '1px solid #1F2937' }}>
                    <div style={{ fontFamily: 'Fredoka One, sans-serif', fontSize: 28, color: stat.color, lineHeight: 1, marginBottom: 5 }}>{stat.value}</div>
                    <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 9, color: '#4B5563', textTransform: 'uppercase', letterSpacing: 1.2 }}>{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Dismiss button */}
              <button
                onClick={() => setShowAllDoneCelebration(false)}
                style={{
                  background: 'linear-gradient(135deg, #F59E0B, #FFD93D)',
                  border: 'none', borderRadius: 20,
                  padding: '16px 52px',
                  fontFamily: 'Fredoka One, sans-serif', fontSize: 22, color: '#111',
                  cursor: 'pointer', letterSpacing: 0.3,
                  boxShadow: '0 6px 28px rgba(245,158,11,0.55)',
                }}
              >
                Keep Going 💪
              </button>
            </div>
          </div>
        );
      })()}

      {celebrateTask && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: C.white, borderRadius: 24, padding: 36, textAlign: "center", maxWidth: 300, animation: "popIn 0.4s ease" }}>
            <div style={{ fontSize: 72, animation: "float 1s ease infinite" }}>🎉</div>
            <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 28, color: C.primary, marginTop: 12 }}>Awesome job!</div>
            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 16, color: C.textMuted, marginTop: 8 }}>{celebrateTask.name}</div>
            <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 22, color: "#FFD93D", marginTop: 12 }}>+{celebrateTask.points} coins ⭐</div>
            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: C.textMuted, marginTop: 8 }}>Waiting for parent approval!</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, padding: "24px 20px 28px", borderRadius: "0 0 28px 28px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 44 }}>{child.avatar || "🧒"}</div>
          <div>
            <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 24, color: "#fff" }}>{child.name} 👋</div>
            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: C.accentLight }}>Keep it up!</div>
          </div>
        </div>
        {nextReward && (
          <div style={{ marginTop: 16, background: "rgba(255,255,255,0.15)", borderRadius: 12, padding: "10px 14px" }}>
            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: C.accentLight, fontWeight: 700, marginBottom: 6 }}>NEXT REWARD: {nextReward.emoji} {nextReward.name}</div>
            <div style={{ background: "rgba(255,255,255,0.2)", borderRadius: 20, height: 8, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.min((totalPoints / nextReward.points) * 100, 100)}%`, background: "#FFD93D", borderRadius: 20, transition: "width 0.5s" }} />
            </div>
            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, color: C.accentLight, marginTop: 4 }}>{Math.max(nextReward.points - totalPoints, 0)} more coins to go!</div>
          </div>
        )}
      </div>

      <div style={{ padding: "20px 16px 0" }}>

        {/* ── Persistent Coin Wallet HUD ── */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 30,
          display: 'flex', justifyContent: 'center',
          paddingBottom: 12,
          background: C.bg, // opaque so content doesn't bleed through when sticky
        }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            background: '#111827',
            border: '1px solid #1F2937',
            borderRadius: 50,
            padding: '8px 20px 8px 14px',
            position: 'relative',
            boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
          }}>
            <span style={{ fontSize: 19, lineHeight: 1 }}>🪙</span>
            <span style={{ fontFamily: 'Fredoka One, sans-serif', fontSize: 22, color: '#F59E0B', lineHeight: 1 }}>
              {walletCoins}
            </span>
            <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 12, color: '#4B5563', lineHeight: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Coins
            </span>
            {/* Floating +N animation */}
            {coinDelta !== null && (
              <div
                key={coinDeltaKey}
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 6,
                  fontFamily: 'Fredoka One, sans-serif',
                  fontSize: 14,
                  color: '#FFD93D',
                  animation: 'coinPop 0.95s ease forwards',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                  textShadow: '0 1px 6px rgba(251,191,36,0.7)',
                }}
              >
                +{coinDelta}
              </div>
            )}
          </div>
        </div>

        {kidScreen === "home" && (
          <>

            {/* ── Personalized Greeting + Daily Completion Ring ── */}
            {(() => {
              const now = new Date();
              const hour = now.getHours();
              const greeting =
                hour < 12 ? 'Good morning' :
                hour < 17 ? 'Good afternoon' :
                             'Good evening';
              const greetingEmoji = hour < 12 ? '👊' : hour < 17 ? '⚡' : '🌙';
              const displayName = localUsername ? `@${localUsername}` : child.name;

              // Use real tasks only (exclude un-approved kid suggestions)
              const realTasks = myTasks.filter(t => !(t.suggested_by_kid && t.suggestion_status === 'pending'));
              const totalMissions = realTasks.length;
              const doneMissions = realTasks.filter(t => t.status === 'pending' || t.status === 'completed').length;
              const ringPct = totalMissions > 0 ? doneMissions / totalMissions : 0;
              const allDone = totalMissions > 0 && doneMissions === totalMissions;
              const noneDone = doneMissions === 0;

              const motiveLine =
                totalMissions === 0 ? 'No missions set yet — check back soon! 🌟' :
                allDone           ? 'All missions complete! You crushed it today 🏆' :
                noneDone          ? `You've got ${totalMissions} mission${totalMissions !== 1 ? 's' : ''} today — let's get it 💪` :
                                    `You've completed ${doneMissions} of ${totalMissions} missions — keep going! 🔥`;

              // SVG ring geometry
              const size = 140;
              const cx = 70, cy = 70, r = 52, sw = 12;
              const circumference = 2 * Math.PI * r;
              // Start fully empty; once ringAnimated flips true the CSS transition draws it
              const arcOffset = ringAnimated ? circumference * (1 - ringPct) : circumference;
              const ringColor = allDone ? '#FFD93D' : '#F59E0B';

              return (
                <div style={{ background: '#111827', borderRadius: 22, padding: '22px 20px 20px', marginBottom: 20, textAlign: 'center' }}>
                  {/* Greeting */}
                  <div style={{ fontFamily: 'Fredoka One, sans-serif', fontSize: 24, color: '#F9FAFB', lineHeight: 1.25, marginBottom: 5 }}>
                    {greeting}, {displayName}! {greetingEmoji}
                  </div>
                  <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: 13, color: '#9CA3AF', marginBottom: 20, minHeight: 18 }}>
                    {motiveLine}
                  </div>

                  {/* Completion ring */}
                  <div style={{ display: 'inline-block', position: 'relative' }}>
                    <svg
                      width={size} height={size}
                      style={{
                        display: 'block',
                        animation: allDone ? 'ringPulse 2.2s ease infinite' : 'none',
                      }}
                    >
                      {/* Background track */}
                      <circle
                        cx={cx} cy={cy} r={r}
                        fill="none"
                        stroke="#374151"
                        strokeWidth={sw}
                      />
                      {/* Progress arc */}
                      <circle
                        cx={cx} cy={cy} r={r}
                        fill="none"
                        stroke={ringColor}
                        strokeWidth={sw}
                        strokeLinecap="round"
                        strokeDasharray={`${circumference}`}
                        strokeDashoffset={`${arcOffset}`}
                        transform={`rotate(-90, ${cx}, ${cy})`}
                        style={{
                          transition: 'stroke-dashoffset 0.9s cubic-bezier(0.4, 0, 0.2, 1), stroke 0.4s',
                        }}
                      />
                    </svg>
                    {/* Center label */}
                    <div style={{
                      position: 'absolute', inset: 0,
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      pointerEvents: 'none',
                    }}>
                      <div style={{ fontFamily: 'Fredoka One, sans-serif', fontSize: 26, color: allDone ? '#FFD93D' : '#F9FAFB', lineHeight: 1, letterSpacing: -0.5 }}>
                        {doneMissions}&nbsp;/&nbsp;{totalMissions}
                      </div>
                      <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 10, color: '#6B7280', marginTop: 5, textTransform: 'uppercase', letterSpacing: 1 }}>
                        missions today
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ── Missions-left countdown ── */}
            {activeTasks.length > 0 && (() => {
              const left = activeTasks.length;
              const { icon, msg, color } =
                left >= 4 ? { icon: '🎯', msg: `${left} missions left today`,       color: '#9CA3AF' } :
                left === 3 ? { icon: '⚡', msg: "Only 3 left — you're moving!",      color: '#F59E0B' } :
                left === 2 ? { icon: '🔥', msg: 'Just 2 more — almost there!',      color: '#F97316' } :
                             { icon: '👊', msg: 'ONE mission left — finish strong!', color: '#EF4444' };
              return (
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 14, color, letterSpacing: 0.1 }}>
                    {icon} {msg}
                  </span>
                </div>
              );
            })()}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 24 }}>
              <StatCard label="To Do" value={activeTasks.length} emoji="🎯" />
              <StatCard label="Waiting" value={pendingTasks.length} emoji="⏳" color={C.warning} />
              <StatCard label="Done" value={completedTasks.length} emoji="✅" color={C.success} />
            </div>

            {activeTasks.length === 0 ? (
              <Card style={{ textAlign: "center", padding: 32, background: C.surfaceGreen, marginBottom: 24 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
                <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 22, color: C.primary }}>All done!</div>
                <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 14, color: C.textMuted, marginTop: 8 }}>No missions right now. Great job! 🌟</div>
              </Card>
            ) : (
              <div style={{ marginBottom: 24 }}>

                {/* ── Almost-done nudge banner ── */}
                {(activeTasks.length === 1 || activeTasks.length === 2) && (
                  <div style={{
                    background: 'linear-gradient(135deg, #F59E0B, #FFD93D)',
                    borderRadius: 14,
                    padding: '13px 18px',
                    marginBottom: 16,
                    textAlign: 'center',
                    animation: 'nudgeDrop 0.38s cubic-bezier(0.34,1.56,0.64,1) both',
                    boxShadow: '0 4px 16px rgba(245,158,11,0.35)',
                  }}>
                    <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 15, color: '#111', lineHeight: 1.3 }}>
                      {activeTasks.length === 2
                        ? "You're almost done! 💪 Finish strong!"
                        : "Last one! You've got this! 🏆"}
                    </div>
                  </div>
                )}

                <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 22, color: C.text, marginBottom: 14 }}>My Missions Today 🎯</div>

                {/* Quick Win card — lowest-point active task */}
                {quickWinTask && (
                  <div style={{ background: "linear-gradient(135deg, #FFFBEB, #FEF3C7)", borderRadius: 18, border: "2px solid #F59E0B", padding: "16px", display: "flex", alignItems: "center", gap: 14, marginBottom: 14, boxShadow: "0 4px 16px rgba(245,158,11,0.18)", position: "relative", overflow: "hidden" }}>
                    {/* Amber glow strip */}
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg, #F59E0B, #FCD34D, #F59E0B)", borderRadius: "18px 18px 0 0" }} />
                    <div style={{ fontSize: 38 }}>{quickWinTask.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                        <span style={{ background: "#F59E0B", color: "#fff", fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 10, padding: "2px 7px", borderRadius: 20, letterSpacing: 0.5 }}>⚡ QUICK WIN</span>
                      </div>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 15, color: "#92400E" }}>{quickWinTask.name}</div>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: "#B45309" }}>{quickWinTask.frequency} · <span style={{ color: "#D97706", fontWeight: 700 }}>+{quickWinTask.points} ⭐</span> · <span style={{ color: "#9CA3AF" }}>{estTime(quickWinTask.points)}</span></div>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, color: "#B45309", marginTop: 2, fontStyle: "italic" }}>Start here — easiest first!</div>
                    </div>
                    <button onClick={() => handleTaskDone(quickWinTask)} style={{ background: "linear-gradient(135deg, #F59E0B, #FBBF24)", border: "none", borderRadius: 12, color: "#fff", fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 13, padding: "10px 14px", cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 2px 8px rgba(245,158,11,0.4)" }}>Done! ✓</button>
                  </div>
                )}

                {/* Remaining missions */}
                {activeTasks.filter(t => t.id !== quickWinTask?.id).map(task => (
                  <div key={task.id} style={{ background: C.white, borderRadius: 16, border: `2px solid ${C.border}`, padding: "16px", display: "flex", alignItems: "center", gap: 14, marginBottom: 12, boxShadow: "0 2px 12px rgba(45,106,79,0.06)" }}>
                    <div style={{ fontSize: 36 }}>{task.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 15, color: C.text }}>{task.name}</div>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: C.textMuted }}>{task.frequency} · <span style={{ color: "#FFD93D", fontWeight: 700 }}>+{task.points} ⭐</span> · <span style={{ color: "#9CA3AF" }}>{estTime(task.points)}</span></div>
                    </div>
                    <button onClick={() => handleTaskDone(task)} style={{ background: `linear-gradient(135deg, ${C.success}, ${C.primaryLight})`, border: "none", borderRadius: 12, color: "#fff", fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 13, padding: "10px 14px", cursor: "pointer", whiteSpace: "nowrap" }}>Done! ✓</button>
                  </div>
                ))}
              </div>
            )}

            {pendingTasks.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 20, color: C.text, marginBottom: 12 }}>Waiting for Approval ⏳</div>
                {pendingTasks.map(task => (
                  <div key={task.id} style={{ background: C.warningLight, borderRadius: 14, border: `1px solid #FFD93D`, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                    <div style={{ fontSize: 28 }}>{task.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: C.text }}>{task.name}</div>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: C.textMuted }}>Waiting for parent to approve ⏳</div>
                    </div>
                    <Badge color="#FFD93D33" textColor="#B7791F">+{task.points} ⭐</Badge>
                  </div>
                ))}
              </div>
            )}

            {suggestedTasks.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 20, color: C.text, marginBottom: 12 }}>My Suggestions 💡</div>
                {suggestedTasks.map(task => (
                  <div key={task.id} style={{ background: C.surface, borderRadius: 14, border: `1.5px dashed ${C.border}`, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                    <div style={{ fontSize: 28 }}>{task.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: C.text }}>{task.name}</div>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: C.textMuted }}>⏳ Waiting for parent to review</div>
                    </div>
                    <Badge color="#FEF9C3" textColor="#92400E">+{task.points} ⭐</Badge>
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginBottom: 24 }}>
              {!showSuggestMission ? (
                <button onClick={() => setShowSuggestMission(true)} style={{ width: "100%", padding: "13px", borderRadius: 12, border: `2px dashed ${C.primary}`, background: "transparent", color: C.primary, fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
                  Suggest a Mission ➕
                </button>
              ) : (
                <div style={{ background: C.surface, borderRadius: 16, border: `2px solid ${C.primary}`, padding: "20px" }}>
                  <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 18, color: C.text, marginBottom: 16 }}>Suggest a Mission 💡</div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 6 }}>Mission name</div>
                  <input autoFocus value={suggestMissionForm.name} onChange={e => setSuggestMissionForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Help with dishes" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1.5px solid ${C.border}`, fontFamily: "Nunito, sans-serif", fontSize: 14, color: C.text, background: C.surfaceGreen, outline: "none", boxSizing: "border-box", marginBottom: 14 }} />
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 8 }}>Pick an emoji</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                    {SUGGEST_TASK_EMOJIS.map(e => (
                      <button key={e} onClick={() => setSuggestMissionForm(f => ({ ...f, emoji: e }))} style={{ fontSize: 20, padding: "6px 10px", borderRadius: 10, border: `2px solid ${suggestMissionForm.emoji === e ? C.primary : "transparent"}`, background: suggestMissionForm.emoji === e ? C.accentLighter : C.surfaceGreen, cursor: "pointer" }}>{e}</button>
                    ))}
                  </div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 6 }}>Suggested coins (max 50)</div>
                  <input type="number" min={1} max={50} value={suggestMissionForm.points} onChange={e => setSuggestMissionForm(f => ({ ...f, points: e.target.value }))} style={{ width: 100, padding: "10px 12px", borderRadius: 10, border: `1.5px solid ${C.border}`, fontFamily: "Nunito, sans-serif", fontSize: 14, color: C.text, background: C.surfaceGreen, outline: "none", marginBottom: 16 }} />
                  <div style={{ display: "flex", gap: 10 }}>
                    <button onClick={submitSuggestedMission} disabled={suggestMissionSaving || !suggestMissionForm.name.trim()} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, color: "#fff", fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, cursor: "pointer", opacity: !suggestMissionForm.name.trim() ? 0.5 : 1 }}>
                      {suggestMissionSaving ? "Submitting…" : "Submit for Approval 🚀"}
                    </button>
                    <button onClick={() => { setShowSuggestMission(false); setSuggestMissionForm({ name: '', emoji: '⭐', points: '10' }); }} style={{ padding: "12px 16px", borderRadius: 12, border: `1.5px solid ${C.border}`, background: C.surface, color: C.textMuted, fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {kidScreen === "rewards" && (
          <div>
            <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 26, color: C.text, marginBottom: 20 }}>My Rewards 🏆</div>
            <div style={{ background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, borderRadius: 16, padding: "20px", textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 48, color: "#FFD93D" }}>{totalPoints}</div>
              <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 14, color: C.accentLight, fontWeight: 700 }}>Total Coins ⭐</div>
            </div>
            {activeRewards.length === 0 ? (
              <Card style={{ textAlign: "center", padding: 32, background: C.surfaceGreen }}>
                <div style={{ fontSize: 48 }}>🏆</div>
                <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 15, color: C.textMuted, marginTop: 12 }}>No rewards set up yet. Ask a parent!</div>
              </Card>
            ) : activeRewards.map(reward => {
              const canRedeem = totalPoints >= reward.points;
              const isPending = reward.status === "pending_redemption";
              const isRedeemed = reward.status === "redeemed";
              return (
                <div key={reward.id} style={{ background: isRedeemed ? "#F0FDF4" : isPending ? "#FFF3E0" : canRedeem ? C.successLight : C.white, border: `2px solid ${isRedeemed ? C.success : isPending ? C.warning : canRedeem ? C.success : C.border}`, borderRadius: 16, padding: "16px 20px", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{ fontSize: 36 }}>{reward.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 15, color: C.text }}>{reward.name}</div>
                      <div style={{ background: C.border, borderRadius: 20, height: 6, marginTop: 8, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${Math.min((totalPoints / reward.points) * 100, 100)}%`, background: canRedeem ? C.success : C.primaryLighter, borderRadius: 20, transition: "width 0.5s" }} />
                      </div>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                        {isRedeemed ? "🎉 Redeemed!" : isPending ? "⏳ Waiting for parent approval" : canRedeem ? "✅ You can request this!" : `${reward.points - totalPoints} more coins needed`}
                      </div>
                    </div>
                    <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 18, color: canRedeem ? C.success : C.textMuted }}>{reward.points}⭐</div>
                  </div>
                  {canRedeem && !isPending && !isRedeemed && (
                    <button onClick={async () => {
                      await supabase.from('rewards').update({ status: 'pending_redemption' }).eq('id', reward.id);
                      try {
                        await supabase.functions.invoke('notify-parent', {
                          body: {
                            type: 'reward',
                            reward_name: reward.name,
                            child_name: child.name,
                            parent_id: child.parent_id,
                          },
                        });
                      } catch (err) {
                        console.error('Failed to send reward notification:', err);
                      }
                      window.location.reload();
                    }} style={{ width: "100%", marginTop: 12, padding: "10px", borderRadius: 10, border: "none", background: C.primary, color: "#fff", fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
                      🎁 Request This Reward!
                    </button>
                  )}
                </div>
              );
            })}

            {suggestedRewards.length > 0 && (
              <div style={{ marginTop: 24, marginBottom: 16 }}>
                <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 20, color: C.text, marginBottom: 12 }}>My Suggestions 💡</div>
                {suggestedRewards.map(reward => (
                  <div key={reward.id} style={{ background: C.surface, borderRadius: 14, border: `1.5px dashed ${C.border}`, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                    <div style={{ fontSize: 32 }}>{reward.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, color: C.text }}>{reward.name}</div>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: C.textMuted }}>⏳ Waiting for parent to review</div>
                    </div>
                    <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 16, color: C.textMuted }}>{reward.points}⭐</div>
                  </div>
                ))}
              </div>
            )}

            {/* ── My Goals / GOAT Savings Pod ── */}
            <div style={{ marginTop: 28, marginBottom: 8 }}>
              <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 22, color: C.text, marginBottom: 14 }}>My Goals 🎯</div>

              {goals.length === 0 && (
                <div style={{ background: C.surface, borderRadius: 14, border: `1.5px dashed ${C.border}`, padding: "20px", textAlign: "center", marginBottom: 14 }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>🐐</div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 14, color: C.textMuted }}>No goals yet — add one below and start saving in your GOAT Pod!</div>
                </div>
              )}

              {goals.map(goal => {
                const isReached = goal.saved_coins >= goal.target_coins;
                const pct = goal.target_coins > 0 ? Math.min((goal.saved_coins / goal.target_coins) * 100, 100) : 0;
                const isOpen = savePodGoalId === goal.id;
                const maxSave = Math.min(walletCoins, Math.max(0, goal.target_coins - goal.saved_coins));
                return (
                  <div key={goal.id} style={{ background: isReached ? "#F0FDF4" : C.white, borderRadius: 16, border: `2px solid ${isReached ? "#86EFAC" : C.border}`, padding: "16px 18px", marginBottom: 12, boxShadow: "0 2px 10px rgba(45,106,79,0.06)" }}>

                    {/* Goal header */}
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                      <div style={{ fontSize: 34 }}>{goal.emoji}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 15, color: C.text }}>{goal.name}</div>
                        <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: C.textMuted }}>🎯 Goal: {goal.target_coins} coins</div>
                      </div>
                      {isReached && (
                        <span style={{ background: "#DCFCE7", color: "#166534", fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 11, padding: "4px 10px", borderRadius: 20 }}>🎉 GOAT Goal Reached!</span>
                      )}
                    </div>

                    {/* GOAT Savings Pod panel */}
                    <div style={{ background: isReached ? "#DCFCE7" : "#FFFBEB", borderRadius: 12, padding: "10px 14px", border: `1px solid ${isReached ? "#86EFAC" : "#FDE68A"}`, marginBottom: isReached ? 0 : 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <span style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: isReached ? "#166534" : "#92400E" }}>🐐 GOAT Savings Pod</span>
                        <span style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: isReached ? "#16A34A" : "#B45309" }}>
                          {goal.saved_coins} / {goal.target_coins} coins saved
                        </span>
                      </div>
                      <div style={{ background: isReached ? "#BBF7D0" : "#FDE68A", borderRadius: 20, height: 8, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: isReached ? "#22C55E" : "#F59E0B", borderRadius: 20, transition: "width 0.5s ease" }} />
                      </div>
                      {!isReached && (
                        <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, color: "#B45309", marginTop: 5 }}>
                          {goal.target_coins - goal.saved_coins} more coins to go!
                        </div>
                      )}
                    </div>

                    {/* GOAT Save Coins button / inline input / goal reached message */}
                    {isReached ? null : isOpen ? (
                      <div>
                        <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, fontWeight: 700, color: C.textMuted, marginBottom: 6 }}>
                          How many coins do you want to save? <span style={{ color: C.primary }}>(you have {walletCoins})</span>
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <input
                            autoFocus
                            type="number"
                            min={1}
                            max={maxSave}
                            value={saveAmount}
                            onChange={e => { setSaveAmount(e.target.value); setSaveError(''); }}
                            onKeyDown={e => { if (e.key === 'Enter') handleSaveCoins(goal); if (e.key === 'Escape') { setSavePodGoalId(null); setSaveAmount(''); setSaveError(''); } }}
                            placeholder={`1–${maxSave}`}
                            style={{ flex: 1, padding: "9px 12px", borderRadius: 10, border: `1.5px solid ${saveError ? "#EF4444" : C.primary}`, fontFamily: "Nunito, sans-serif", fontSize: 14, color: C.text, background: C.surfaceGreen, outline: "none" }}
                          />
                          <button
                            onClick={() => handleSaveCoins(goal)}
                            disabled={savePodSaving || !saveAmount}
                            style={{ padding: "9px 16px", borderRadius: 10, border: "none", background: `linear-gradient(135deg, #F59E0B, #FBBF24)`, color: "#fff", fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 13, cursor: "pointer", opacity: !saveAmount ? 0.5 : 1, whiteSpace: "nowrap" }}
                          >
                            {savePodSaving ? "Saving…" : "Confirm ✓"}
                          </button>
                          <button
                            onClick={() => { setSavePodGoalId(null); setSaveAmount(''); setSaveError(''); }}
                            style={{ padding: "9px 12px", borderRadius: 10, border: `1.5px solid ${C.border}`, background: C.surface, color: C.textMuted, fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                          >
                            ✕
                          </button>
                        </div>
                        {saveError ? (
                          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: "#EF4444", marginTop: 6, fontWeight: 700 }}>
                            ⚠️ {saveError}
                          </div>
                        ) : walletCoins === 0 ? (
                          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: "#EF4444", marginTop: 6 }}>
                            You don't have any coins yet — complete some missions first!
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <button
                        onClick={() => { setSavePodGoalId(goal.id); setSaveAmount(''); setSaveError(''); }}
                        disabled={maxSave === 0}
                        style={{ width: "100%", padding: "10px", borderRadius: 12, border: `2px solid #F59E0B`, background: maxSave > 0 ? "linear-gradient(135deg, #FFFBEB, #FEF3C7)" : C.surface, color: maxSave > 0 ? "#92400E" : C.textMuted, fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, cursor: maxSave > 0 ? "pointer" : "not-allowed", opacity: maxSave === 0 ? 0.5 : 1 }}
                      >
                        🐐 ➕ Save Coins to GOAT Pod
                      </button>
                    )}
                  </div>
                );
              })}

              {/* Add a Goal form */}
              {!showAddGoal ? (
                <button
                  onClick={() => setShowAddGoal(true)}
                  style={{ width: "100%", padding: "13px", borderRadius: 12, border: `2px dashed #F59E0B`, background: "transparent", color: "#B45309", fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, cursor: "pointer", marginBottom: 8 }}
                >
                  ➕ Add a Goal
                </button>
              ) : (
                <div style={{ background: "#FFFBEB", borderRadius: 16, border: `2px solid #F59E0B`, padding: "20px", marginBottom: 8 }}>
                  <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 18, color: C.text, marginBottom: 14 }}>New GOAT Goal 🐐</div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 6 }}>What are you saving for?</div>
                  <input
                    autoFocus
                    value={addGoalForm.name}
                    onChange={e => setAddGoalForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. New bike, video game…"
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1.5px solid #FDE68A`, fontFamily: "Nunito, sans-serif", fontSize: 14, color: C.text, background: C.white, outline: "none", boxSizing: "border-box", marginBottom: 14 }}
                  />
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 8 }}>Pick an emoji</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                    {GOAL_EMOJIS.map(e => (
                      <button key={e} onClick={() => setAddGoalForm(f => ({ ...f, emoji: e }))} style={{ fontSize: 20, padding: "6px 10px", borderRadius: 10, border: `2px solid ${addGoalForm.emoji === e ? "#F59E0B" : "transparent"}`, background: addGoalForm.emoji === e ? "#FEF3C7" : C.surfaceGreen, cursor: "pointer" }}>{e}</button>
                    ))}
                  </div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 6 }}>Coin target</div>
                  <input
                    type="number"
                    min={1}
                    value={addGoalForm.target}
                    onChange={e => setAddGoalForm(f => ({ ...f, target: e.target.value }))}
                    style={{ width: 100, padding: "10px 12px", borderRadius: 10, border: `1.5px solid #FDE68A`, fontFamily: "Nunito, sans-serif", fontSize: 14, color: C.text, background: C.white, outline: "none", marginBottom: 16 }}
                  />
                  <div style={{ display: "flex", gap: 10 }}>
                    <button
                      onClick={submitGoal}
                      disabled={addGoalSaving || !addGoalForm.name.trim()}
                      style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #F59E0B, #FBBF24)", color: "#fff", fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, cursor: "pointer", opacity: !addGoalForm.name.trim() ? 0.5 : 1 }}
                    >
                      {addGoalSaving ? "Saving…" : "Save Goal 🎯"}
                    </button>
                    <button
                      onClick={() => { setShowAddGoal(false); setAddGoalForm({ name: '', emoji: '🎯', target: '50' }); }}
                      style={{ padding: "12px 16px", borderRadius: 12, border: `1.5px solid ${C.border}`, background: C.surface, color: C.textMuted, fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer" }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop: suggestedRewards.length > 0 ? 8 : 24, marginBottom: 24 }}>
              {!showSuggestDrop ? (
                <button onClick={() => setShowSuggestDrop(true)} style={{ width: "100%", padding: "13px", borderRadius: 12, border: `2px dashed ${C.primary}`, background: "transparent", color: C.primary, fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
                  Suggest a Reward ➕
                </button>
              ) : (
                <div style={{ background: C.surface, borderRadius: 16, border: `2px solid ${C.primary}`, padding: "20px" }}>
                  <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 18, color: C.text, marginBottom: 16 }}>Suggest a Reward 💡</div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 6 }}>Reward name</div>
                  <input autoFocus value={suggestDropForm.name} onChange={e => setSuggestDropForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Movie night" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1.5px solid ${C.border}`, fontFamily: "Nunito, sans-serif", fontSize: 14, color: C.text, background: C.surfaceGreen, outline: "none", boxSizing: "border-box", marginBottom: 14 }} />
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 8 }}>Pick an emoji</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                    {SUGGEST_REWARD_EMOJIS.map(e => (
                      <button key={e} onClick={() => setSuggestDropForm(f => ({ ...f, emoji: e }))} style={{ fontSize: 20, padding: "6px 10px", borderRadius: 10, border: `2px solid ${suggestDropForm.emoji === e ? C.primary : "transparent"}`, background: suggestDropForm.emoji === e ? C.accentLighter : C.surfaceGreen, cursor: "pointer" }}>{e}</button>
                    ))}
                  </div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 6 }}>Suggested coin cost</div>
                  <input type="number" min={1} value={suggestDropForm.points} onChange={e => setSuggestDropForm(f => ({ ...f, points: e.target.value }))} style={{ width: 100, padding: "10px 12px", borderRadius: 10, border: `1.5px solid ${C.border}`, fontFamily: "Nunito, sans-serif", fontSize: 14, color: C.text, background: C.surfaceGreen, outline: "none", marginBottom: 16 }} />
                  <div style={{ display: "flex", gap: 10 }}>
                    <button onClick={submitSuggestedDrop} disabled={suggestDropSaving || !suggestDropForm.name.trim()} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, color: "#fff", fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, cursor: "pointer", opacity: !suggestDropForm.name.trim() ? 0.5 : 1 }}>
                      {suggestDropSaving ? "Submitting…" : "Submit for Approval 🚀"}
                    </button>
                    <button onClick={() => { setShowSuggestDrop(false); setSuggestDropForm({ name: '', emoji: '🎮', points: '30' }); }} style={{ padding: "12px 16px", borderRadius: 12, border: `1.5px solid ${C.border}`, background: C.surface, color: C.textMuted, fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {kidScreen === "me" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 72, marginBottom: 12 }}>{child.avatar || "🧒"}</div>
            {editingUsername ? (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: C.textMuted, marginBottom: 8 }}>Letters and numbers only, max 20 chars</div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <span style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 22, color: C.primary }}>@</span>
                  <input
                    autoFocus
                    value={usernameInput}
                    onChange={e => setUsernameInput(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 20))}
                    placeholder="coolkid123"
                    style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 22, color: C.text, border: `2px solid ${C.primary}`, borderRadius: 10, padding: "6px 12px", outline: "none", width: 160, textAlign: "center" }}
                    onKeyDown={e => { if (e.key === 'Enter') saveUsername(); if (e.key === 'Escape') setEditingUsername(false); }}
                  />
                </div>
                <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 12 }}>
                  <button onClick={saveUsername} disabled={usernameSaving} style={{ background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, border: "none", borderRadius: 10, color: "#fff", fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, padding: "8px 20px", cursor: "pointer" }}>{usernameSaving ? "Saving…" : "Save ✓"}</button>
                  <button onClick={() => setEditingUsername(false)} style={{ background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: 10, color: C.textMuted, fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, padding: "8px 16px", cursor: "pointer" }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: 4 }}>
                {localUsername ? (
                  <button onClick={() => { setUsernameInput(localUsername); setEditingUsername(true); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                    <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 30, color: C.text }}>@{localUsername}</div>
                  </button>
                ) : (
                  <div>
                    <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 30, color: C.text }}>{child.name}</div>
                    <button onClick={() => { setUsernameInput(''); setEditingUsername(true); }} style={{ background: "none", border: `1.5px dashed ${C.primary}`, borderRadius: 10, color: C.primary, fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 13, padding: "4px 14px", cursor: "pointer", marginTop: 6 }}>+ Set your username</button>
                  </div>
                )}
              </div>
            )}
            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 14, color: C.textMuted, marginBottom: 20 }}>Age {child.age}</div>

            {/* Level & XP progress */}
            {(() => {
              const { level, currentXP, nextXP, pct } = getLevelProgress(child.xp || 0);
              return (
                <div style={{ background: "#111827", borderRadius: 18, padding: "20px 22px", marginBottom: 20, textAlign: "left" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 900, fontSize: 26, color: "#fff", letterSpacing: -0.5 }}>
                      Level <span style={{ color: "#FFD93D" }}>{level}</span>
                    </div>
                    <div style={{ background: "#FFD93D", borderRadius: 8, padding: "4px 12px" }}>
                      <span style={{ fontFamily: "Nunito, sans-serif", fontWeight: 900, fontSize: 12, color: "#111" }}>LEVEL {level}</span>
                    </div>
                  </div>
                  <div style={{ background: "#374151", borderRadius: 20, height: 12, overflow: "hidden", marginBottom: 8 }}>
                    <div style={{ height: "100%", width: `${Math.min(pct, 100)}%`, background: "linear-gradient(90deg, #FFD93D, #F59E0B)", borderRadius: 20, transition: "width 0.6s ease" }} />
                  </div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 13, color: "#9CA3AF" }}>
                    {level < 10 ? `${currentXP} / ${nextXP} XP` : `${currentXP} XP — Max Level!`}
                  </div>
                </div>
              );
            })()}

            {/* Streak card */}
            {(() => {
              const streak = child.streak || 0;
              const isOnFire = streak >= 7;
              const milestoneMsg =
                streak >= 30 ? "30 days! Legend 👑" :
                streak >= 14 ? "Two weeks! 🏆" :
                streak >= 7  ? "One week strong! 💪" : null;

              if (streak === 0) {
                return (
                  <div style={{ background: C.white, border: `2px solid ${C.border}`, borderRadius: 18, padding: "22px", marginBottom: 20, textAlign: "center" }}>
                    <div style={{ fontSize: 52, lineHeight: 1, marginBottom: 10, filter: "grayscale(0.6)", opacity: 0.5 }}>🔥</div>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 900, fontSize: 17, color: C.text }}>Start your streak today!</div>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: C.textMuted, marginTop: 4 }}>Complete a mission to light your fire</div>
                  </div>
                );
              }

              return (
                <div style={{
                  background: isOnFire ? "linear-gradient(135deg, #FFFBEB, #FEF3C7)" : "linear-gradient(135deg, #FFF7ED, #FFEDD5)",
                  border: `2px solid ${isOnFire ? "#F59E0B" : "#FB923C"}`,
                  borderRadius: 18,
                  padding: "22px",
                  marginBottom: 20,
                  textAlign: "center",
                  boxShadow: isOnFire ? "0 0 36px rgba(251, 191, 36, 0.5), 0 4px 16px rgba(245, 158, 11, 0.2)" : "none",
                }}>
                  <div style={{ fontSize: 56, lineHeight: 1, marginBottom: 2 }}>🔥</div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 900, fontSize: 64, color: isOnFire ? "#B45309" : "#EA580C", lineHeight: 1, marginBottom: 2 }}>{streak}</div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 11, color: isOnFire ? "#D97706" : "#C2410C", letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>DAY STREAK</div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: isOnFire ? "#92400E" : "#9A3412" }}>
                    {isOnFire ? "You're on fire!" : "Keep it going!"}
                  </div>
                  {milestoneMsg && (
                    <div style={{ marginTop: 12, background: isOnFire ? "#FDE68A" : "#FED7AA", borderRadius: 10, padding: "8px 16px", display: "inline-block" }}>
                      <span style={{ fontFamily: "Nunito, sans-serif", fontWeight: 900, fontSize: 14, color: isOnFire ? "#78350F" : "#9A3412" }}>{milestoneMsg}</span>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Weekly Challenge card */}
            {(() => {
              const dayOfWeek = new Date().getDay();
              const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
              const monday = new Date();
              monday.setDate(monday.getDate() - daysFromMonday);
              const mondayISO = monday.toISOString().split('T')[0];
              const isCurrentWeek = child.weekly_challenge_start === mondayISO;
              const daysComplete = isCurrentWeek ? Math.min(child.weekly_tasks_completed || 0, 7) : 0;
              const isComplete = daysComplete >= 7;
              const pct = (daysComplete / 7) * 100;

              if (isComplete) {
                return (
                  <div style={{ background: "linear-gradient(135deg, #111827, #1F2937)", borderRadius: 18, padding: "24px 22px", marginBottom: 20, textAlign: "center", animation: "weeklyPulse 2.4s ease infinite" }}>
                    <div style={{ fontSize: 52, lineHeight: 1, marginBottom: 10 }}>🏆</div>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 900, fontSize: 20, color: "#FFD93D", marginBottom: 4 }}>Weekly Challenge Complete!</div>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 17, color: "#FDE68A", marginBottom: 8 }}>+100 XP 🎉</div>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: "#6B7280" }}>You crushed every day this week!</div>
                    <div style={{ display: "flex", gap: 5, marginTop: 16, justifyContent: "center" }}>
                      {[...Array(7)].map((_, i) => (
                        <div key={i} style={{ width: 28, height: 8, borderRadius: 4, background: "#FFD93D" }} />
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <div style={{ background: "#111827", borderRadius: 18, padding: "20px 22px", marginBottom: 20, textAlign: "left" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 900, fontSize: 15, color: "#fff", letterSpacing: 0.2 }}>WEEKLY CHALLENGE</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
                      <span style={{ fontFamily: "Nunito, sans-serif", fontWeight: 900, fontSize: 22, color: "#FFD93D" }}>{daysComplete}</span>
                      <span style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 13, color: "#4B5563" }}> / 7 days</span>
                    </div>
                  </div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: "#6B7280", marginBottom: 14 }}>Complete all missions every day — earn +100 XP</div>
                  <div style={{ display: "flex", gap: 5, marginBottom: 12 }}>
                    {["M","T","W","T","F","S","S"].map((day, i) => (
                      <div key={i} style={{ flex: 1, textAlign: "center" }}>
                        <div style={{ background: i < daysComplete ? "#FFD93D" : "#1F2937", border: i < daysComplete ? "none" : "1px solid #374151", borderRadius: 6, height: 26, marginBottom: 4, transition: "background 0.3s" }} />
                        <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 10, color: i < daysComplete ? "#FFD93D" : "#4B5563" }}>{day}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ background: "#1F2937", borderRadius: 20, height: 8, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg, #F59E0B, #FFD93D)", borderRadius: 20, transition: "width 0.6s ease" }} />
                  </div>
                </div>
              );
            })()}

            {/* Week in Review card + Share button */}
            {(() => {
              const dayOfWeek = new Date().getDay();
              const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
              const monday = new Date();
              monday.setDate(monday.getDate() - daysFromMonday);
              const mondayISO = monday.toISOString().split('T')[0];
              const isCurrentWeek = child.weekly_challenge_start === mondayISO;
              const daysComplete = isCurrentWeek ? Math.min(child.weekly_tasks_completed || 0, 7) : 0;
              const isWeeklyComplete = daysComplete >= 7;
              const weekMissions = completedTasks.length + pendingTasks.length;
              const weekCoins = totalPoints;
              const { level, currentXP } = getLevelProgress(child.xp || 0);
              const streak = child.streak || 0;
              const displayName = localUsername ? `@${localUsername}` : child.name;

              const tagline =
                isWeeklyComplete ? "Absolute legend this week! 🏆" :
                daysComplete >= 5 ? "Almost a perfect week! Keep going 💪" :
                streak >= 7 ? "On fire and unstoppable 🔥" :
                weekMissions > 0 ? "Crushing it one mission at a time ⭐" :
                "Every journey starts with one step 🌟";

              const shareText = `I completed ${weekMissions} mission${weekMissions !== 1 ? 's' : ''} this week on ThriveTrack! 🔥 Level ${level} · ${streak} day streak${isWeeklyComplete ? ' · Weekly Challenge Complete! 🏆' : ''}`;

              const handleShare = async () => {
                if (navigator.share) {
                  try { await navigator.share({ title: 'My Week on ThriveTrack', text: shareText }); } catch (e) { /* user cancelled */ }
                } else {
                  navigator.clipboard.writeText(shareText);
                  setShareCopied(true);
                  setTimeout(() => setShareCopied(false), 2500);
                }
              };

              return (
                <div style={{ marginBottom: 24 }}>
                  {/* Card */}
                  <div style={{ background: "linear-gradient(160deg, #111827 60%, #1a1f2e)", borderRadius: 22, padding: "28px 24px 22px", position: "relative", overflow: "hidden", boxShadow: "0 8px 40px rgba(0,0,0,0.35)", textAlign: "left" }}>
                    {/* Decorative ambient glow */}
                    <div style={{ position: "absolute", top: -50, right: -50, width: 160, height: 160, borderRadius: "50%", background: "radial-gradient(circle, rgba(251,191,36,0.15) 0%, transparent 70%)", pointerEvents: "none" }} />
                    <div style={{ position: "absolute", bottom: -30, left: -30, width: 120, height: 120, borderRadius: "50%", background: "radial-gradient(circle, rgba(167,139,250,0.1) 0%, transparent 70%)", pointerEvents: "none" }} />

                    {/* Header */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                      <div>
                        <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 900, fontSize: 10, color: "#4B5563", letterSpacing: 2.5, textTransform: "uppercase", marginBottom: 5 }}>WEEK IN REVIEW</div>
                        <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 24, color: "#fff", lineHeight: 1 }}>{displayName}</div>
                      </div>
                      <div style={{ fontSize: 44, filter: "drop-shadow(0 2px 8px rgba(251,191,36,0.3))" }}>{child.avatar || "🧒"}</div>
                    </div>

                    {/* Stats 2×2 grid */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                      {[
                        { label: "LEVEL", value: level, sub: `${currentXP} XP`, color: "#FFD93D" },
                        { label: "STREAK", value: `${streak}🔥`, sub: streak === 1 ? "day" : "days", color: "#F97316" },
                        { label: "MISSIONS", value: weekMissions, sub: "done ✅", color: "#34D399" },
                        { label: "COINS", value: weekCoins, sub: "total ⭐", color: "#A78BFA" },
                      ].map(stat => (
                        <div key={stat.label} style={{ background: "rgba(31,41,55,0.8)", borderRadius: 14, padding: "14px 16px", border: "1px solid #1F2937" }}>
                          <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 900, fontSize: 9, color: "#6B7280", letterSpacing: 2, textTransform: "uppercase", marginBottom: 5 }}>{stat.label}</div>
                          <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 28, color: stat.color, lineHeight: 1, marginBottom: 3 }}>{stat.value}</div>
                          <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 11, color: "#4B5563" }}>{stat.sub}</div>
                        </div>
                      ))}
                    </div>

                    {/* Weekly challenge status row */}
                    {isCurrentWeek && (
                      <div style={{ background: isWeeklyComplete ? "linear-gradient(90deg, rgba(146,64,14,0.6), rgba(180,83,9,0.6))" : "rgba(31,41,55,0.8)", borderRadius: 13, padding: "11px 16px", marginBottom: 18, display: "flex", alignItems: "center", gap: 10, border: isWeeklyComplete ? "1px solid #B45309" : "1px solid #1F2937" }}>
                        <span style={{ fontSize: 24 }}>{isWeeklyComplete ? "🏆" : "⚡"}</span>
                        <div>
                          <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 900, fontSize: 13, color: isWeeklyComplete ? "#FFD93D" : "#D1D5DB", lineHeight: 1, marginBottom: 2 }}>
                            Weekly Challenge — {daysComplete}/7 days
                          </div>
                          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, color: isWeeklyComplete ? "#FDE68A" : "#6B7280" }}>
                            {isWeeklyComplete ? "+100 XP earned this week 🎉" : `${7 - daysComplete} more day${7 - daysComplete !== 1 ? 's' : ''} to go`}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Tagline + branding */}
                    <div style={{ borderTop: "1px solid #1F2937", paddingTop: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 13, color: "#9CA3AF", fontStyle: "italic", flex: 1 }}>{tagline}</div>
                      <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 14, color: "#374151", whiteSpace: "nowrap", letterSpacing: 0.3 }}>ThriveTrack</div>
                    </div>
                  </div>

                  {/* Share button */}
                  <button
                    onClick={handleShare}
                    style={{
                      marginTop: 12, width: "100%", padding: "14px",
                      borderRadius: 14, border: "none",
                      background: shareCopied
                        ? "linear-gradient(135deg, #059669, #10B981)"
                        : "linear-gradient(135deg, #F59E0B, #FFD93D)",
                      color: shareCopied ? "#fff" : "#111",
                      fontFamily: "Nunito, sans-serif", fontWeight: 900, fontSize: 16,
                      cursor: "pointer", transition: "all 0.25s", letterSpacing: 0.2,
                      boxShadow: shareCopied ? "0 4px 20px rgba(5,150,105,0.35)" : "0 4px 20px rgba(245,158,11,0.35)",
                    }}
                  >
                    {shareCopied ? "✓ Copied to clipboard!" : "Share My Week 📲"}
                  </button>
                </div>
              );
            })()}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
              <StatCard label="My Coins" value={totalPoints} emoji="⭐" />
              <StatCard label="Missions Done" value={completedTasks.length} emoji="✅" color={C.success} />
            </div>
            <Card style={{ background: `linear-gradient(135deg, ${C.accentLighter}, ${C.white})`, textAlign: "center", padding: 24, marginBottom: 16 }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>💬</div>
              <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 18, color: C.primary }}>
                {AFFIRMATIONS[Math.floor(Math.random() * AFFIRMATIONS.length)]}
              </div>
            </Card>

            <Card style={{ marginBottom: 16, textAlign: "left" }}>
              <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 18, color: C.text, marginBottom: 4 }}>🏅 My Badges</div>
              <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: C.textMuted, marginBottom: 16 }}>{earnedBadges.length} of {BADGES.length} earned</div>
              {earnedBadges.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, fontWeight: 700, color: C.success, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>✅ Earned</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {earnedBadges.map(badge => (
                      <div key={badge.id} style={{ background: C.surfaceGreen, border: `2px solid ${C.success}`, borderRadius: 14, padding: "10px 14px", textAlign: "center", minWidth: 80 }}>
                        <div style={{ fontSize: 28 }}>{badge.emoji}</div>
                        <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 11, color: C.text, marginTop: 4 }}>{badge.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, fontWeight: 700, color: C.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>🔒 Locked</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {unearnedBadges.map(badge => (
                    <div key={badge.id} style={{ background: darkMode ? "#1F2937" : "#f3f4f6", border: `2px solid ${darkMode ? "#374151" : "#e5e7eb"}`, borderRadius: 14, padding: "10px 14px", textAlign: "center", minWidth: 80, opacity: 0.6 }}>
                      <div style={{ fontSize: 28, filter: "grayscale(1)" }}>{badge.emoji}</div>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 11, color: C.textMuted, marginTop: 4 }}>{badge.name}</div>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 10, color: C.textMuted, marginTop: 2 }}>{badge.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            {/* Dark Mode Toggle */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 20px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 22 }}>🌙</span>
                <div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, color: C.text }}>Dark Mode</div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: C.textMuted }}>Easier on the eyes at night</div>
                </div>
              </div>
              <button
                onClick={() => { const next = !darkMode; setDarkMode(next); localStorage.setItem('kid_dark_mode', String(next)); }}
                aria-label="Toggle dark mode"
                style={{ width: 52, height: 28, borderRadius: 14, border: "none", padding: 0, background: darkMode ? "#F59E0B" : C.border, position: "relative", cursor: "pointer", flexShrink: 0, transition: "background 0.25s" }}
              >
                <div style={{ position: "absolute", top: 3, left: darkMode ? 27 : 3, width: 22, height: 22, borderRadius: 11, background: "#fff", transition: "left 0.2s ease", boxShadow: "0 1px 4px rgba(0,0,0,0.3)" }} />
              </button>
            </div>

            {/* Change PIN */}
            {!showChangePIN ? (
              <div
                onClick={() => { setShowChangePIN(true); setChangePINForm({ current: '', newPin: '', confirm: '' }); setPinChangeError(''); setPinChangeSuccess(false); }}
                style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 20px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 22 }}>🔑</span>
                  <div>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, color: C.text }}>Change My PIN</div>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: C.textMuted }}>Update your 4-digit login PIN</div>
                  </div>
                </div>
                <span style={{ fontSize: 18, color: C.textMuted }}>›</span>
              </div>
            ) : (
              <div style={{ background: C.surface, border: `2px solid ${C.primary}`, borderRadius: 16, padding: "20px", marginBottom: 16 }}>
                <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 18, color: C.text, marginBottom: 16 }}>Change My PIN 🔑</div>
                {pinChangeSuccess ? (
                  <div style={{ textAlign: "center", padding: 16 }}>
                    <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 15, color: C.success }}>PIN updated!</div>
                  </div>
                ) : (
                  <>
                    {[['current', 'Current PIN'], ['newPin', 'New PIN'], ['confirm', 'Confirm New PIN']].map(([field, label]) => (
                      <div key={field} style={{ marginBottom: 12 }}>
                        <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 6 }}>{label}</div>
                        <input
                          type="password"
                          maxLength={4}
                          value={changePINForm[field]}
                          onChange={e => { if (/^\d{0,4}$/.test(e.target.value)) setChangePINForm(f => ({ ...f, [field]: e.target.value })); }}
                          placeholder="••••"
                          style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${C.border}`, fontFamily: "Nunito, sans-serif", fontSize: 22, color: C.text, background: C.surfaceGreen, outline: "none", boxSizing: "border-box", letterSpacing: 8, textAlign: "center" }}
                        />
                      </div>
                    ))}
                    {pinChangeError && <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: "#EF4444", marginBottom: 10 }}>{pinChangeError}</div>}
                    <div style={{ display: "flex", gap: 10 }}>
                      <button onClick={changePIN} disabled={pinChangeSaving} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, color: "#fff", fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
                        {pinChangeSaving ? "Saving…" : "Update PIN ✓"}
                      </button>
                      <button onClick={() => { setShowChangePIN(false); setPinChangeError(''); }} style={{ padding: "12px 16px", borderRadius: 12, border: `1.5px solid ${C.border}`, background: C.surface, color: C.textMuted, fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Cancel</button>
                    </div>
                  </>
                )}
              </div>
            )}

            <button onClick={onLogout} style={{ width: "100%", padding: "14px", borderRadius: 12, border: `2px solid ${C.border}`, background: C.white, fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, color: C.textMuted, cursor: "pointer" }}>
              👋 Log Out
            </button>
          </div>
        )}

        {kidScreen === "journal" && (
          <div style={{ textAlign: "left" }}>
            <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 26, color: C.text, marginBottom: 4 }}>📓 My Journal</div>
            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: C.textMuted, marginBottom: 20 }}>Write your thoughts and earn coins!</div>

            <Card style={{ marginBottom: 16 }}>
              <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 16, color: C.text, marginBottom: 12 }}>How are you feeling today?</div>
              <div style={{ display: "flex", justifyContent: "space-around", marginBottom: 16 }}>
                {[["😄","Amazing"],["😊","Good"],["😐","Okay"],["😔","Sad"],["😤","Frustrated"]].map(([emoji, label]) => (
                  <button key={emoji} onClick={() => setSelectedMood(emoji)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, background: selectedMood === emoji ? C.accentLighter : "transparent", border: `2px solid ${selectedMood === emoji ? C.primary : "transparent"}`, borderRadius: 12, padding: "8px 6px", cursor: "pointer", transition: "all 0.2s" }}>
                    <span style={{ fontSize: 32 }}>{emoji}</span>
                    <span style={{ fontFamily: "Nunito, sans-serif", fontSize: 10, fontWeight: 700, color: selectedMood === emoji ? C.primary : C.textMuted }}>{label}</span>
                  </button>
                ))}
              </div>

              {journalSubmitted ? (
                <div style={{ textAlign: "center", padding: 16 }}>
                  <div style={{ fontSize: 40, marginBottom: 8 }}>🌟</div>
                  <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 18, color: C.success }}>+5 coins earned!</div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: C.textMuted, marginTop: 4 }}>Great job writing today!</div>
                  {todayJournal && (
                    <div style={{ marginTop: 12, padding: 12, background: C.surfaceGreen, borderRadius: 10, textAlign: "left" }}>
                      {todayJournal.mood && <div style={{ fontSize: 24, marginBottom: 4 }}>{todayJournal.mood}</div>}
                      <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: C.text }}>{todayJournal.entry}</div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <textarea
                    value={journalEntry}
                    onChange={e => setJournalEntry(e.target.value)}
                    placeholder="How are you feeling today? What happened? What are you grateful for?"
                    style={{ width: "100%", minHeight: 120, padding: "10px 12px", borderRadius: 10, border: `2px solid ${C.border}`, fontFamily: "Nunito, sans-serif", fontSize: 13, color: C.text, background: C.surfaceGreen, outline: "none", resize: "vertical", boxSizing: "border-box" }}
                  />
                  <button onClick={submitJournal} disabled={journalSaving || !journalEntry.trim()} style={{ width: "100%", marginTop: 10, padding: "12px", borderRadius: 10, border: "none", background: journalEntry.trim() ? C.primary : C.border, color: journalEntry.trim() ? "#fff" : C.textMuted, fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, cursor: journalEntry.trim() ? "pointer" : "default" }}>
                    {journalSaving ? "Saving..." : "Submit Journal ⭐"}
                  </button>
                </>
              )}
            </Card>

            {pastJournals.filter(j => j.date !== today).length > 0 && (
              <div>
                <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 18, color: C.text, marginBottom: 12 }}>Past Entries</div>
                {pastJournals.filter(j => j.date !== today).map(journal => (
                  <Card key={journal.id} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      {journal.mood && <span style={{ fontSize: 22 }}>{journal.mood}</span>}
                      <span style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: C.textMuted }}>{new Date(journal.created_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                      {journal.parent_hearted && <span style={{ marginLeft: "auto", fontSize: 18 }}>❤️</span>}
                    </div>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: C.text, lineHeight: 1.5, marginBottom: journal.parent_comment ? 8 : 0 }}>{journal.entry}</div>
                    {journal.parent_comment && (
                      <div style={{ background: C.accentLighter, borderRadius: 10, padding: "8px 12px", borderLeft: `3px solid ${C.primary}` }}>
                        <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, fontWeight: 700, color: C.primary, marginBottom: 2 }}>💬 Parent said:</div>
                        <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: C.text }}>{journal.parent_comment}</div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

      </div>

      <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 480, background: C.white, borderTop: `1px solid ${C.border}`, display: "flex", zIndex: 100, boxShadow: "0 -4px 20px rgba(0,0,0,0.08)" }}>
        {[["🏠","Home","home"],["🏆","Rewards","rewards"],["📓","Journal","journal"],["😊","Me","me"]].map(([icon, label, s]) => (
          <button key={s} onClick={() => { if (s === "me") handleMeScreen(); else if (s === "journal") { setKidScreen("journal"); loadTodayJournal(); } else setKidScreen(s); }} style={{ flex: 1, padding: "12px 0", border: "none", background: "transparent", cursor: "pointer", color: kidScreen === s ? C.primary : C.textLight, fontFamily: "Nunito, sans-serif", fontSize: 10, fontWeight: 700, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, borderTop: kidScreen === s ? `3px solid ${C.primary}` : "3px solid transparent", transition: "all 0.2s" }}>
            <span style={{ fontSize: 22 }}>{icon}</span>{label}
          </button>
        ))}
      </div>
    </div>
  );
}
